import React, { useEffect, useState } from "react";
import { ListMusic, Plus, Trash2, Play, Star, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * HostPlaylistsPanel — build & manage pre-loaded playlists for a radio show.
 *
 * Hosts can save named sets (e.g. "Opening set", "Rush hour drive", "After-
 * hours") from the station library then load them straight into a deck with
 * one tap. Marking a playlist as DEFAULT auto-loads it when the host opens
 * the studio for that station.
 */
export default function HostPlaylistsPanel({ slug, tracks = [], onLoadTrack }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", track_ids: [] });
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/radio/host/playlists/", { params: { station_slug: slug } });
      setPlaylists(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Playlist needs a name"); return; }
    if (!form.track_ids.length) { toast.error("Add at least one track"); return; }
    try {
      await api.post("/radio/host/playlists/", {
        station_slug: slug,
        name: form.name.trim(), description: form.description.trim(),
        track_ids: form.track_ids, is_default: false,
      });
      toast.success("Playlist saved"); setCreating(false);
      setForm({ name: "", description: "", track_ids: [] }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleDefault = async (p) => {
    try {
      await api.patch(`/radio/host/playlists/${p.id}`, { is_default: !p.is_default });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTrack = async (p, trackId) => {
    const nextIds = (p.track_ids || []).filter((tid) => tid !== trackId);
    try { await api.patch(`/radio/host/playlists/${p.id}`, { track_ids: nextIds }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addTrack = async (p, trackId) => {
    if ((p.track_ids || []).includes(trackId)) return;
    try { await api.patch(`/radio/host/playlists/${p.id}`, { track_ids: [...(p.track_ids || []), trackId] }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete playlist "${p.name}"?`)) return;
    try { await api.delete(`/radio/host/playlists/${p.id}`); load(); toast.success("Playlist deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const loadIntoDeck = (p, deck) => {
    const first = (p.tracks || []).find((t) => !!t?.source_url);
    if (!first) { toast.error("This playlist has no playable tracks"); return; }
    onLoadTrack?.(first, deck);
    toast.success(`Loaded "${first.title}" on Deck ${deck}`);
  };

  return (
    <div data-testid="host-playlists-panel" className="bg-[#0A0D14] border border-white/10 rounded-lg p-3 space-y-3">
      <div className="flex items-center gap-2">
        <ListMusic className="w-4 h-4 text-tv-accent" />
        <div className="font-black text-sm flex-1">My playlists</div>
        <button
          data-testid="host-playlists-create-open"
          onClick={() => setCreating((v) => !v)}
          className="text-[10px] font-black bg-tv-accent text-black px-2 py-1 rounded inline-flex items-center gap-1"
        ><Plus className="w-3 h-3" /> New</button>
      </div>

      {creating && (
        <div className="bg-black/40 border border-white/10 rounded p-2 space-y-2" data-testid="host-playlists-new-form">
          <input
            data-testid="host-playlists-new-name"
            placeholder="Name (e.g. 'Opening set')"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          />
          <input
            placeholder="Short description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          />
          <div className="max-h-32 overflow-y-auto space-y-0.5">
            {tracks.length === 0 && <div className="text-white/40 text-[11px] italic">Station library is empty — upload tracks first.</div>}
            {tracks.map((t) => {
              const sel = form.track_ids.includes(t.id);
              return (
                <label key={t.id} className="flex items-center gap-2 text-xs text-white/80 bg-white/5 rounded px-1.5 py-1 cursor-pointer hover:bg-white/10">
                  <input
                    type="checkbox" checked={sel}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      track_ids: e.target.checked ? [...f.track_ids, t.id] : f.track_ids.filter((x) => x !== t.id),
                    }))}
                    className="accent-tv-accent"
                  />
                  <span className="truncate flex-1">{t.title}</span>
                  <span className="text-white/40 truncate">{t.artist}</span>
                </label>
              );
            })}
          </div>
          <div className="flex gap-1.5">
            <button
              data-testid="host-playlists-new-save"
              onClick={save}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs py-1.5 rounded"
            >Save · {form.track_ids.length} track{form.track_ids.length === 1 ? "" : "s"}</button>
            <button
              onClick={() => { setCreating(false); setForm({ name: "", description: "", track_ids: [] }); }}
              className="bg-white/5 hover:bg-white/15 text-white/80 text-xs px-3 rounded"
            >Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-white/60 text-xs py-2">Loading playlists…</div>
      ) : playlists.length === 0 ? (
        <div className="text-white/40 text-xs text-center py-4">No playlists yet. Build one to load a whole show with one tap.</div>
      ) : (
        <div className="space-y-1.5">
          {playlists.map((p) => {
            const isOpen = openId === p.id;
            return (
              <div key={p.id} data-testid={`host-playlist-${p.id}`} className="bg-white/5 border border-white/10 rounded">
                <div className="flex items-center gap-2 p-2">
                  <button
                    data-testid={`host-playlist-default-${p.id}`}
                    onClick={() => toggleDefault(p)}
                    title={p.is_default ? "Default set" : "Mark as default"}
                    className={`p-1 rounded ${p.is_default ? "text-tv-accent" : "text-white/30 hover:text-white"}`}
                  ><Star className={`w-3.5 h-3.5 ${p.is_default ? "fill-tv-accent" : ""}`} /></button>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setOpenId(isOpen ? null : p.id)}
                      data-testid={`host-playlist-toggle-${p.id}`}
                      className="w-full text-left"
                    >
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-white/50 text-[11px] truncate">{(p.tracks || p.track_ids || []).length} tracks{p.description ? ` · ${p.description}` : ""}</div>
                    </button>
                  </div>
                  <button
                    data-testid={`host-playlist-load-a-${p.id}`}
                    onClick={() => loadIntoDeck(p, "A")}
                    className="bg-sky-500/20 hover:bg-sky-500 hover:text-black text-sky-200 border border-sky-500/40 text-[10px] font-black px-2 py-1 rounded inline-flex items-center gap-1"
                    title="Load first track on Deck A"
                  ><Play className="w-3 h-3" /> A</button>
                  <button
                    data-testid={`host-playlist-load-b-${p.id}`}
                    onClick={() => loadIntoDeck(p, "B")}
                    className="bg-rose-500/20 hover:bg-rose-500 hover:text-black text-rose-200 border border-rose-500/40 text-[10px] font-black px-2 py-1 rounded inline-flex items-center gap-1"
                    title="Load first track on Deck B"
                  ><Play className="w-3 h-3" /> B</button>
                  <button
                    data-testid={`host-playlist-del-${p.id}`}
                    onClick={() => del(p)}
                    className="text-red-300 hover:text-red-100 p-1"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </div>

                {isOpen && (
                  <div className="px-2 pb-2 space-y-1 border-t border-white/10 pt-2">
                    <div className="space-y-0.5 max-h-40 overflow-y-auto">
                      {(p.tracks || []).length === 0 && <div className="text-white/40 text-[11px] italic">No playable tracks in this set.</div>}
                      {(p.tracks || []).map((t) => (
                        <div key={t.id} className="flex items-center gap-1.5 text-[11px] bg-black/40 rounded px-1.5 py-1">
                          <div className="flex-1 min-w-0 truncate text-white/90">{t.title} <span className="text-white/40">· {t.artist}</span></div>
                          <button
                            onClick={() => onLoadTrack?.(t, "A")}
                            className="bg-sky-500/20 hover:bg-sky-500 hover:text-black text-sky-200 text-[9px] font-black px-1.5 py-0.5 rounded"
                          >A</button>
                          <button
                            onClick={() => onLoadTrack?.(t, "B")}
                            className="bg-rose-500/20 hover:bg-rose-500 hover:text-black text-rose-200 text-[9px] font-black px-1.5 py-0.5 rounded"
                          >B</button>
                          <button
                            onClick={() => removeTrack(p, t.id)}
                            className="text-red-300/80 hover:text-red-200"
                            title="Remove from playlist"
                          ><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                    {tracks.length > 0 && (
                      <details className="text-[11px] text-white/60">
                        <summary className="cursor-pointer">+ Add from station library</summary>
                        <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                          {tracks.filter((t) => !(p.track_ids || []).includes(t.id)).map((t) => (
                            <button
                              key={t.id}
                              onClick={() => addTrack(p, t.id)}
                              className="w-full text-left bg-white/5 hover:bg-white/15 rounded px-1.5 py-1 truncate"
                            >+ {t.title} · <span className="text-white/50">{t.artist}</span></button>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
